import React, { useState, useEffect } from 'react'
import { Copy, Check } from 'lucide-react'

interface CreatePrivateGameModalProps {
  onClose: () => void
  onGameCreated: (gameNumber: number, password: string) => void
}

// AGC: Generate random 5-character password
const generatePassword = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // Excluded confusing chars: I, O, 0, 1
  let password = ''
  for (let i = 0; i < 5; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return password
}

// AGC: Generate game number starting from 1000
const generateGameNumber = (): number => {
  // AGC: TODO - In production, this should come from backend to ensure uniqueness
  return 1000 + Math.floor(Math.random() * 9000) // Random number between 1000-9999
}

// AGC: Create Private Game Modal - Shows game number and password with copy functionality
export const CreatePrivateGameModal: React.FC<CreatePrivateGameModalProps> = ({
  onClose,
  onGameCreated,
}) => {
  const [gameNumber, setGameNumber] = useState<number>(0)
  const [password, setPassword] = useState<string>('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    // AGC: Generate game number and password when modal opens
    setGameNumber(generateGameNumber())
    setPassword(generatePassword())
  }, [])

  const gameCode = `${gameNumber}#${password}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(gameCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('[CreatePrivateGame] Failed to copy:', error)
    }
  }

  const handleCreate = () => {
    onGameCreated(gameNumber, password)
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="w-full max-w-sm bg-slate-950 border border-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* AGC: Header */}
        <div className="px-4 py-3 border-b border-white/10">
          <h3 className="text-base font-semibold text-white">Create Private Game</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Share this code with your friend</p>
        </div>

        {/* AGC: Content */}
        <div className="px-4 py-4 space-y-3">
          {/* AGC: Game Number Display */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1">GAME NUMBER</p>
            <p className="text-3xl font-bold text-white tracking-wider">#{gameNumber}</p>
          </div>

          {/* AGC: Password Display */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1">PASSWORD</p>
            <p className="text-3xl font-bold text-purple-300 tracking-[0.3em]">{password}</p>
          </div>

          {/* AGC: Combined Game Code with Copy Button */}
          <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-400/40 rounded-xl p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <p className="text-[9px] uppercase tracking-[0.25em] text-slate-400 mb-1">GAME CODE</p>
                <p className="text-lg font-mono font-bold text-white break-all">{gameCode}</p>
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-xs">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-slate-300" />
                    <span className="text-slate-300 text-xs">Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* AGC: Info */}
          <div className="bg-slate-900/40 rounded-lg p-2.5">
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Copy the game code and share it with your friend. They can join using the "Join Game" option.
            </p>
          </div>
        </div>

        {/* AGC: Actions */}
        <div className="px-4 py-3 border-t border-white/10 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-700 text-sm text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-sm font-semibold text-white hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg shadow-green-500/30"
          >
            Create Game
          </button>
        </div>
      </div>
    </div>
  )
}

